import React, { createContext, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { calculateProgress } from "../functions";
import {
  PROGRESS_ALL,
  STORAGE_KEY_LEARNING_VIEW,
  STORAGE_KEY_FILTER_STATUS,
  STORAGE_KEY_SORT_ORDER,
} from "../constants";
import { initialState, FILTER_INITIAL_STATE } from "./initialState";
import { dataFetcherService } from "../services/dataFetcherService";
import { sortService } from "../services/sortService";
import { filterService } from "../services/filterService";
import { cacheService } from "../services/cacheService";

export const MyLearningContext = createContext();

/**
 * Provider component for MyLearning context
 * Manages the state and operations for the MyLearning feature
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 */
const MyLearningProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  const updateState = useCallback((updates) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      return newState;
    });
  }, []);

  const resetProvider = useCallback(() => {
    setState((prev) => ({
      ...initialState,
      filter: FILTER_INITIAL_STATE,
      viewLimit: prev.viewLimit,
    }));
  }, []);

  const setListView = useCallback((newListView) => {
    localStorage.setItem(STORAGE_KEY_LEARNING_VIEW, newListView);
    updateState({ listView: newListView });
  }, []);

  const setMaximumNumberCoursesToBeDisplayed = useCallback(async () => {
    try {
      const amountCoursesToDisplay =
        await dataFetcherService.fetchMaxCoursesDisplayed();
      updateState({ noOfCourses: parseInt(amountCoursesToDisplay, 2) });
    } catch (error) {
      console.error("Failed to fetch maximum number of courses:", error);
    }
  }, []);

  const getFilteredItems = (filter, items) => {
    const filteredItems = filterService.createFilteredItems(items, filter);
    return sortService.updateList(
      filteredItems,
      filter,
      filter.status || PROGRESS_ALL,
    );
  };

  const getFilteredCourses = (filter, courses = state.courses) => {
    const filteredCourses = courses.filter((course) => !course.isProgram) || [];
    return getFilteredItems(filter, filteredCourses);
  };

  const getFilteredPrograms = (filter, programs = state.programs) => {
    return getFilteredItems(filter, programs);
  };

  const checkIsAdmin = useCallback(
    (searchText, courses) => {
      return filterService.checkIsAdmin(searchText, courses, state.isAdmin);
    },
    [state.isAdmin],
  );

  const sortPrograms = (courses, filterOverride = state.filter) => {
    if (!Array.isArray(courses)) {
      updateState({ programs: [], filteredPrograms: [] });
      return;
    }
    const newPrograms = courses.filter((course) => course.isProgram) || [];
    const newFilteredPrograms = getFilteredPrograms(
      filterOverride,
      newPrograms,
    );
    updateState({
      programs: newPrograms,
      filteredPrograms: newFilteredPrograms,
    });
  };

  const sortFilteredPrograms = (programs, filterOverride = state.filter) => {
    const newFilteredPrograms = getFilteredPrograms(filterOverride, programs);
    updateState({
      filteredPrograms: newFilteredPrograms,
    });
  };

  const sortCourses = useCallback(
    (coursesInfo, searchText, filterOverride = state.filter) => {
      const { courses } = coursesInfo;
      const newCourses = courses.filter((course) => !course.isProgram) || [];
      const newFilteredCourses = getFilteredCourses(filterOverride, newCourses);
      updateState({
        trainingCategories: coursesInfo.trainingCategories,
        courses: newCourses,
        filteredCourses: newFilteredCourses,
        isAdmin: checkIsAdmin(searchText, newCourses),
      });
    },
    [checkIsAdmin],
  );

  const sortFilteredCourses = (coursesInfo, filterOverride = state.filter) => {
    const newFilteredCourses = getFilteredCourses(
      filterOverride,
      coursesInfo.courses,
    );
    updateState({ filteredCourses: newFilteredCourses });
  };

  const fetchMyLearningData = useCallback(
    async (filterOptions, fetchFilteredContent = false) => {
      if (!filterOptions) {
        console.log("fetchMyLearningData called without filterOptions");
        return;
      }

      updateState({ searching: true, filter: filterOptions });
      try {
        const personalId = filterOptions.personalId || state.personalId;
        const cacheKey = cacheService.generateKey(personalId, filterOptions);
        let coursesInfo = cacheService.get(cacheKey);

        if (!coursesInfo) {
          coursesInfo = await dataFetcherService.fetchCourses({
            ...filterOptions,
            personalId,
          });
          // cacheService.set(cacheKey, coursesInfo);
        }

        // Process data consistently regardless of source
        if (fetchFilteredContent) {
          // Use server-filtered content: avoid re-applying tag filter client-side
          const filterForPostProcessing = {
            ...filterOptions,
            selectedTags: Array.isArray(filterOptions.selectedTags)
              ? filterOptions.selectedTags.map(() => 0)
              : [],
          };
          sortFilteredCourses(coursesInfo, filterForPostProcessing);
          const newPrograms =
            coursesInfo.courses.filter((course) => course.isProgram) || [];
          sortFilteredPrograms(newPrograms, filterForPostProcessing);
        } else {
          // Prefer filterOptions to avoid stale state during initial fetches
          sortCourses(coursesInfo, filterOptions.searchText, filterOptions);
          setupCategories(coursesInfo.trainingCategories);
          sortPrograms(coursesInfo.courses, filterOptions);
        }
        calculateTotalPercentCompleted(coursesInfo.courses);
      } catch (error) {
        console.error("Error fetching learning data:", error);
        throw error;
      } finally {
        updateState({ searching: false });
      }
    },
    [state.personalId, sortCourses, calculateTotalPercentCompleted],
  );

  const createCategoryMap = useCallback((trainingCategories) => {
    return trainingCategories.reduce((acc, tc) => {
      acc[tc.trainingCategoryId] = tc;
      return acc;
    }, {});
  }, []);

  const createCategoryHierarchy = useCallback((trainingCategories) => {
    return trainingCategories.reduce((acc, tc) => {
      if (!acc[tc.parentId]) {
        acc[tc.parentId] = [];
      }
      acc[tc.parentId].push(tc);
      return acc;
    }, {});
  }, []);

  const initializeSelectedTags = useCallback((newTags) => {
    return [...newTags.map(() => 0), 0, 0, 0, 0, 0].slice(
      0,
      Math.max(5, newTags.length),
    );
  }, []);

  const setupCategories = (trainingCategories) => {
    const newTrainingCategories = trainingCategories;
    const newCategories = createCategoryMap(newTrainingCategories);
    const newCategoryHierarchy = createCategoryHierarchy(newTrainingCategories);
    updateState({
      categories: newCategories,
      categoryHierarchy: newCategoryHierarchy,
    });
  };

  const setupTags = useCallback(
    async (filterToUse = null) => {
      const newTags = await dataFetcherService.fetchTags();

      updateState({
        tags: newTags,
        filter: {
          ...(filterToUse || state.filter),
          selectedTags: initializeSelectedTags(newTags),
        },
      });
    },
    [state.filter, initializeSelectedTags],
  );

  const getMyLearningData = useCallback(
    async (newPersonalId) => {
      if (!newPersonalId) {
        console.log(
          "getMyLearningData called without personalId. This function should only be called explicitly.",
        );
        return;
      }

      try {
        await resetProvider();
        updateState({ personalId: newPersonalId });

        const resetFilterOptions = {
          ...FILTER_INITIAL_STATE,
          personalId: newPersonalId,
        };
        const cacheKey = cacheService.generateKey(
          newPersonalId,
          resetFilterOptions,
        );
        const cachedData = cacheService.get(cacheKey);

        if (cachedData && cacheService.validateCache(cachedData)) {
          sortCourses(cachedData, resetFilterOptions.searchText);
          setupCategories(cachedData.trainingCategories);
          sortPrograms(cachedData.courses);
          calculateTotalPercentCompleted(cachedData.courses);
        } else {
          await fetchMyLearningData({
            ...FILTER_INITIAL_STATE,
            personalId: newPersonalId,
          });
        }
        await setMaximumNumberCoursesToBeDisplayed();
        await setupTags(FILTER_INITIAL_STATE);
      } catch (error) {
        console.error("Error in getMyLearningData:", error);
        throw error;
      }
    },
    [
      resetProvider,
      state.filter,
      state.noOfCourses,
      state.tags,
      setupTags,
      fetchMyLearningData,
      setMaximumNumberCoursesToBeDisplayed,
      sortCourses,
      setupCategories,
      sortPrograms,
      calculateTotalPercentCompleted,
    ],
  );

  const handleRemoveCourse = useCallback(
    async (courseId) => {
      try {
        const updatedCourses = state.courses.filter(
          (course) => course.courseId !== courseId,
        );
        updateState({ courses: updatedCourses });
        cacheService.clearCache(state.personalId);
        const newFilter = { ...state.filter };
        handleFilterChange(newFilter, updatedCourses);
      } catch (error) {
        console.error("Error removing course:", error);
        throw error;
      }
    },
    [state.courses, state.filter, state.personalId, handleFilterChange],
  );

  const handleFilterChange = useCallback(
    (newFilter, coursesToFilter = [...state.courses, ...state.programs]) => {
      try {
        const filteredCourses = getFilteredCourses(newFilter, coursesToFilter);
        const filteredPrograms = getFilteredPrograms(newFilter);

        updateState({
          filter: newFilter,
          filteredCourses,
          filteredPrograms,
        });

        calculateTotalPercentCompleted([
          ...filteredCourses.active,
          ...filteredPrograms.active,
        ]);
      } catch (error) {
        console.error("Error handling filter change:", error);
      }
    },
    [getFilteredCourses, getFilteredPrograms, state.courses],
  );

  const handleStatusOrSortChange = useCallback(
    (newFilter) => {
      try {
        // Only update status/sorting on existing filtered data, don't recreate filters
        const updatedFilteredCourses = sortService.updateList(
          state.filteredCourses,
          newFilter,
          newFilter.status || PROGRESS_ALL,
        );
        const updatedFilteredPrograms = sortService.updateList(
          state.filteredPrograms,
          newFilter,
          newFilter.status || PROGRESS_ALL,
        );

        updateState({
          filter: newFilter,
          filteredCourses: updatedFilteredCourses,
          filteredPrograms: updatedFilteredPrograms,
        });

        calculateTotalPercentCompleted([
          ...updatedFilteredCourses.active,
          ...updatedFilteredPrograms.active,
        ]);
      } catch (error) {
        console.error("Error handling status/sort change:", error);
      }
    },
    [state.filteredCourses, state.filteredPrograms],
  );

  const applyFilter = useCallback(
    (nextFilter, coursesToFilter = [...state.courses, ...state.programs]) => {
      handleFilterChange(nextFilter, coursesToFilter);
    },
    [handleFilterChange, state.courses],
  );

  const setStatus = useCallback(
    (status) => {
      const newFilter = { ...state.filter, status };
      localStorage.setItem(STORAGE_KEY_FILTER_STATUS, status);
      handleStatusOrSortChange(newFilter);
    },
    [state.filter, handleStatusOrSortChange],
  );

  const setSortOnly = useCallback(
    (sortBy) => {
      const newFilter = { ...state.filter, sortBy };
      localStorage.setItem(STORAGE_KEY_SORT_ORDER, sortBy);
      handleStatusOrSortChange(newFilter);
    },
    [state.filter, handleStatusOrSortChange],
  );

  const calculateTotalPercentCompleted = (courses) => {
    const numberOfCompletedSteps = Array.isArray(courses)
      ? courses.filter((course) => course.completed === true).length
      : 0;

    const numberOfSteps = courses.length;

    const newTotalPercentCompleted = calculateProgress(
      numberOfCompletedSteps,
      numberOfSteps,
    );

    updateState({ totalPercentCompleted: newTotalPercentCompleted });
  };

  const value = {
    ...state,
    getMyLearningData,
    fetchMyLearningData,
    resetProvider,
    clearFilter: () => handleFilterChange(FILTER_INITIAL_STATE),
    search: (newFilter) => fetchMyLearningData(newFilter),
    setFilter: (newFilter) => handleFilterChange(newFilter),
    applyFilter,
    setStatusOrSort: (newFilter) => handleStatusOrSortChange(newFilter),
    setStatus,
    setSortBy: setSortOnly,
    setFilterTags: (newFilterTags) =>
      updateState({ filter: { ...state.filter, selectedTags: newFilterTags } }),
    setViewLimit: (newViewLimit) => updateState({ viewLimit: newViewLimit }),
    setViewLimitNr: (newViewLimitNr) =>
      updateState({ viewLimitNr: newViewLimitNr }),
    setListView,
    setDisplayViewToggle: (newDisplayViewToggle) =>
      updateState({ displayViewToggle: newDisplayViewToggle }),
    initializeSelectedTags,
    removeCourse: handleRemoveCourse,
  };

  return (
    <MyLearningContext.Provider value={value}>
      {children}
    </MyLearningContext.Provider>
  );
};

MyLearningProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MyLearningProvider;
